import React, { Component } from "react";
import styled from "styled-components";
import { Button, Icon, Slider, Switch } from "@material-ui/core";
import SVG from "svg.js";

import { ds } from "../../../core/models/dataService";
import { $, _ } from "../../../vendor";
import { controls } from "../../../editor/ui";
import { PaletteColorType, AssetType, ASSET_FILETYPE } from "../../../../common/constants";
import { AddAssetsContainer } from "../../views/AddAssets/index";
import { BeautifulDialog, ShowDialog } from "./BaseDialog";
import { renderReactRootJSX } from "../../renderReactRoot";
import { getStaticUrl } from "../../../config";
import { FlexSpacer, Gap10, Gap20 } from "../Gap";
import { renderAdjustmentFilter } from "../../../core/utilities/svgFilters";
import { themeColors } from "../../sharedStyles";
import { LabeledContainer } from "../LabeledContainer";
import { trackActivity, uuid } from "../../../core/utilities/utilities";
import { mergeMediaElementModelDefaults } from "../../../../common/assetUtils";
import Api from "../../../core/api";
import ProgressDialog from "./ProgressDialog";
import { FlexBox } from "../LayoutGrid";

import { MultiInputSlider } from "../../../Components/legacy-components/AuthoringEditorComponents/MultiInputSlider.js";
import { LabelContainer, SmallLabel, StyledIconButton } from "../../../Components/legacy-components/AuthoringEditorComponents/Controls";

import "svg.filter.js";

const FilterImage = styled.div`
  .background {
    position: relative;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  label {
    display: block;
    padding: 3px;
    color: #333;
    text-transform: uppercase;
    text-align: center;
    font-size: 14px;
  }
`;

const SelectedFilter = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  width: 30px;
  height: 30px;
  color: white;
  background: ${themeColors.ui_blue};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 24px;
`;

const optionsWidth = 380;

const ImagePreview = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 64px - 24px - 24px);
  width: calc(100% - ${optionsWidth}px - 20px);
  background: #eee;
`;

const OptionsContainer = styled.div`
    position: relative;
    width: ${optionsWidth}px;
    margin-left: 20px;
`;

const FilterContainer = styled.div`
    position: absolute;
    display: inline-table;
    top: 0;
    left: 0;
    right: 0;
    bottom: 50px;
    display: grid;
    gap: 10px;
    grid-auto-rows: min-content;
    margin-right: -24px;
    padding-right: 24px;
    overflow-x: hidden;
    overflow-y: scroll;

    header {
        color: #333;
        font-size: 14px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: 0.5px;
        text-align: left;
        text-transform: uppercase;
        margin-top: 15px;
    }
`;

const TransformsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-left: -8px;
    margin-right: -2px;
`;

const ButtonsContainer = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: space-between;
    height: 50px;
    padding-top: 6px;
`;

const FilterGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const RestyledIconButton = styled(StyledIconButton)`
&&& {
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
}
`;

const StyledLabelContainer = styled(LabelContainer)`
&&& {
    box-sizing: content-box;
    margin-right: 16px;
}
`;

export class FilterDialog extends Component {
    constructor(props) {
        super(props);

        this.trackChanged = {};

        this.gridRef = React.createRef();

        let filter = props.element.model.filter;
        if (filter == "None" || !filter) {
            filter = "none";
        }

        const elemAsset = props.element.asset.attributes;
        const originalRotation = elemAsset.rotation || 0;

        this.state = {
            rotationOffset: 0,
            flipHorizontal: props.element.model.flipHorizontal || false,
            noBg: false,
            originalAssetId: null,
            originalAssetUrl: null,
            altAssetUrl: null,
            altAssetId: null,
            activeFilterId: 1,
            filterBrightness: props.element.model.filterBrightness ?? 1,
            filterContrast: props.element.model.filterContrast ?? 1,
            filterBlur: props.element.model.filterBlur ?? 0,
            mediaOpacity: props.element.model.mediaOpacity ?? 1,
            filterStrength: props.element.model.filterStrength ?? 1,
            filter,
            gridColCount: 3,
            thumbnailWidth: 0,
            thumbnailHeight: 0,
            previewWidth: 0,
            previewHeight: 0,
            originalRotation,
            colors: {},
        };
    }

    componentDidMount = async () => {
        window.addEventListener("resize", this.onResize);

        const { element } = this.props;

        const elemAsset = element.asset.attributes;
        let originalAssetId = elemAsset.id;
        let altAssetId = null;
        if (elemAsset.alteredAssetId) {
            originalAssetId = elemAsset.alteredAssetId;
            altAssetId = elemAsset.id;
        }

        const originalAssetUrl = await ds.assets
            .getAssetById(originalAssetId, AssetType.IMAGE)
            .then(asset => asset.getURL("original", true));

        let altAssetUrl = null;
        if (altAssetId) {
            altAssetUrl = await ds.assets
                .getAssetById(altAssetId, AssetType.IMAGE)
                .then(asset => asset.getURL("original", true));
        }

        const delta = {
            noBg: elemAsset.noBg ?? false,
            originalAssetId,
            originalAssetUrl,
            altAssetUrl,
            altAssetId,
            // We're setting it here, in order to avoid accessing
            // the theme every time the filter is applied
            colors: this.getColors(),
        };

        await new Promise(resolve => {
            let img = new Image();
            img.onload = () => {
                delta.baseWidth = img.width;
                delta.baseHeight = img.height;
                resolve();
            };
            img.src = originalAssetUrl;
        });

        this.setState(delta);

        this.orientPreviews();
    }

    componentWillUnmount = () => {
        window.removeEventListener("resize", this.onResize);

        // Clean up SVG objects
        if (this.img) {
            this.img.remove();
        }
        if (this.group) {
            this.group.remove();
        }
    }

    onResize = () => {
        this.orientPreviews();
    }

    orientPreviews = (delta = {}) => {
        let {
            noBg,
            rotationOffset,
            originalRotation,
            baseWidth,
            baseHeight,
        } = this.state;

        // Ensure that current state is accounted for
        delta = {
            noBg,
            rotationOffset,
            ...delta,
        };
        rotationOffset = delta.rotationOffset;
        delta.rotationOffset %= 360;

        const totalRotation = (originalRotation + rotationOffset) % 360;
        const transpose = totalRotation % 180 !== 0;

        // Get the original width and height
        const adjustedBaseWidth = transpose ? baseHeight : baseWidth;
        const adjustedBaseHeight = transpose ? baseWidth : baseHeight;

        let isPortrait = adjustedBaseHeight > adjustedBaseWidth;

        // Landscape
        let gridColCount = 3;
        let thumbnailWidth = (optionsWidth - 10 * 2) / 3;
        // Portrait
        if (isPortrait) {
            gridColCount = 4;
            thumbnailWidth = (optionsWidth - 10 * 3) / 4;
        }
        let thumbnailHeight = adjustedBaseHeight / adjustedBaseWidth * thumbnailWidth;

        let previewMaxWidth = window.innerWidth - 64 - 24 - 24 - optionsWidth - 20;
        let previewMaxHeight = window.innerHeight - 64 - 24 - 24;
        let previewWidth = Math.min(previewMaxWidth, adjustedBaseWidth / adjustedBaseHeight * previewMaxHeight);
        let previewHeight = Math.min(previewMaxHeight, adjustedBaseHeight / adjustedBaseWidth * previewMaxWidth);

        this.setState({
            ...delta,
            gridColCount,
            thumbnailWidth,
            thumbnailHeight,
            previewWidth,
            previewHeight,
        });
    }

    onToggleBg = async () => {
        let {
            noBg,
            altAssetId,
            originalAssetId,
            rotationOffset,
            originalRotation,
        } = this.state;
        noBg = !noBg;

        this.trackChanged.bgToggled = true;

        let delta = {
            noBg,
        };

        // If we're flagged to use the alt bg, but we don't
        //   have the alt asset yet, then generate it
        if (
            noBg &&
            !altAssetId
        ) {
            let elemAsset = this.props.element.asset.attributes;

            let quitTransform = false;
            let progressDialog = ShowDialog(ProgressDialog, {
                title: "Removing background...",
                message: (
                    <>
                        Hang tight. This might take a minute. Please
                        <br />
                        don't close or refresh your browser.
                    </>
                ),
                onCancel: () => {
                    quitTransform = true;
                    progressDialog.props.closeDialog();
                }
            });

            let imageUrl = await ds.assets
                .getAssetById(originalAssetId, AssetType.IMAGE)
                .then(async asset => {
                    let url = await asset.getURL("original", true);

                    // Rasterize the asset if it's an SVG
                    if (asset.attributes.fileType === "svg") {
                        url = await new Promise((resolve, reject) => {
                            const img = new Image();
                            img.crossOrigin = "anonymous";
                            img.onload = () => {
                                const canvas = document.createElement("canvas");
                                canvas.width = img.width;
                                canvas.height = img.height;
                                const ctx = canvas.getContext("2d");

                                ctx.drawImage(img, 0, 0);

                                let result = canvas.toDataURL();
                                resolve(result);
                            };
                            img.onerror = err => {
                                reject(err);
                            };
                            img.src = url;
                        });
                    }

                    return url;
                });

            progressDialog.setTask(80, 40000);

            let rotation = (originalRotation + rotationOffset) % 360;
            let { transformUrl } = await Api.cloudinaryTransform.post({
                assetId: originalAssetId,
                imageUrl,
                transformOptions: {
                    effect: "background_removal",
                    angle: rotation,
                }
            });

            if (quitTransform) {
                progressDialog.closeDialog();
                return;
            }

            progressDialog.setTask(99, 3000);

            let transpose = rotation % 180 > 0;

            // Extract the original width and height
            const baseAsset = await ds.assets.getAssetById(originalAssetId, AssetType.IMAGE);
            let {
                w,
                h,
            } = baseAsset.attributes;
            if (transpose) {
                let temp = w;
                w = h;
                h = temp;
            }

            let newAsset = await ds.assets.getOrCreateImage({
                url: transformUrl,
                fileType: ASSET_FILETYPE.PNG,
                assetType: AssetType.IMAGE,
                name: `${elemAsset.name}-no-bg`,
                metadata: {
                    source: elemAsset.source,
                    attribution: elemAsset.attribution,
                    tags: elemAsset.tags,
                    link: elemAsset.link,
                },
                imagePropsOverride: {
                    hasAlpha: true,
                    hasSolidBackground: false,
                    imageBackgroundColor: false,
                    width: w,
                    height: h,
                },
                extra: {
                    alteredAssetId: originalAssetId,
                    noBg: true,
                    rotation,
                },
            });

            let altAssetId = newAsset.id;
            let altAssetUrl = await ds.assets
                .getAssetById(altAssetId, AssetType.IMAGE)
                .then(asset => asset.getURL("original", true));

            progressDialog.setProgress(100);
            progressDialog.props.closeDialog();

            delta = {
                ...delta,
                rotationOffset: 0,
                originalRotation: rotation,
                altAssetId,
                altAssetUrl,
            };
        }

        this.setState(delta);
    }

    setStateAndRefreshEditor = (trackLabel, delta) => {
        // HACK: For Safari, we need to make sure to sync all
        // filter IDs and use a unique ID each time, otherwise
        // sometimes the filter is not applied
        const activeFilterId = this.state.activeFilterId + 1;
        this.setState({
            ...delta,
            activeFilterId,
        });
        document.querySelectorAll("[filter-preview]")
            .forEach(node => node.setAttribute("filter", `url(#filter-preview--${activeFilterId}-adj-filter)`));

        this.trackChanged[trackLabel] = true;

        // make sure to reuse the shared filter ID
        this.props.element.updateFilterVersion(activeFilterId);
    }

    applyFilters = async () => {
        const {
            noBg,
            originalAssetId,
            altAssetId,
            rotationOffset,
            originalRotation,
            flipHorizontal,
            filterBrightness,
            filterContrast,
            filterBlur,
            mediaOpacity,
            filterStrength,
            filter
        } = this.state;

        const {
            element,
            closeDialog
        } = this.props;

        const elemAsset = element.asset.attributes;
        const model = element.model;

        const totalRotation = (originalRotation + rotationOffset) % 360;

        const trackDelta = {};
        if ((elemAsset.noBg ?? false) !== noBg) {
            trackDelta.noBg = noBg;
        }
        if ((elemAsset.rotation ?? 0) !== totalRotation) {
            trackDelta.rotation = totalRotation;
        }
        if ((model.flipHorizontal ?? false) !== flipHorizontal) {
            trackDelta.flipHorizontal = flipHorizontal;
        }
        if ((model.filterBrightness ?? 1) !== filterBrightness) {
            trackDelta.brightness = filterBrightness;
        }
        if ((model.filterContrast ?? 1) !== filterContrast) {
            trackDelta.contrast = filterContrast;
        }
        if ((model.filterBlur ?? 0) !== filterBlur) {
            trackDelta.blur = filterBlur;
        }
        if ((model.mediaOpacity ?? 1) !== mediaOpacity) {
            trackDelta.opacity = mediaOpacity;
        }
        if ((model.filterStrength ?? 1) !== filterStrength) {
            trackDelta.filterStrength = filterStrength;
        }

        if ((model.filter ?? "none") !== filter) {
            trackDelta.filter = filter;
        }

        model.flipHorizontal = flipHorizontal;
        model.filterBrightness = filterBrightness;
        model.filterContrast = filterContrast;
        model.filterBlur = filterBlur;
        model.mediaOpacity = mediaOpacity;
        model.filterStrength = filterStrength;
        model.filter = filter;

        const assetWasPreviouslyAltered = (
            !!altAssetId &&
            (
                noBg ||
                !!originalRotation
            )
        );

        let assetId = assetWasPreviouslyAltered ? altAssetId : originalAssetId;

        // If there's been any additional rotation, save it in the asset itself
        if (rotationOffset) {
            let progressDialog = ShowDialog(ProgressDialog, {
                title: "Saving your changes...",
                message: (
                    <>
                        Hang tight. This might take a minute. Please
                        <br />
                        don't close or refresh your browser.
                    </>
                ),
            });

            progressDialog.setTask(50, 1000);

            const assetUrl = await ds.assets
                .getAssetById(assetId, AssetType.IMAGE)
                .then(asset => asset.getURL("original", true));

            const transpose = rotationOffset % 180 !== 0;
            const assetRotatedUrl = await new Promise(resolve => {
                let img = new Image();
                img.crossOrigin = "anonymous";
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    // Set the canvas dimensions according to the rotated image
                    if (transpose) {
                        canvas.width = img.height;
                        canvas.height = img.width;
                    } else {
                        canvas.width = img.width;
                        canvas.height = img.height;
                    }

                    // Rotate and draw the image
                    ctx.translate(canvas.width / 2, canvas.height / 2);
                    ctx.rotate(rotationOffset * Math.PI / 180);
                    ctx.drawImage(img, -img.width / 2, -img.height / 2);

                    const dataUrl = canvas.toDataURL();
                    resolve(dataUrl);
                };

                img.src = assetUrl;
            });

            progressDialog.setTask(99, 3000);

            // Extract the original width and height
            const baseAsset = await ds.assets.getAssetById(assetId, AssetType.IMAGE);
            let {
                w,
                h,
            } = baseAsset.attributes;
            if (transpose) {
                let temp = w;
                w = h;
                h = temp;
            }

            const newAsset = await ds.assets.getOrCreateImage({
                url: assetRotatedUrl,
                fileType: ASSET_FILETYPE.PNG,
                assetType: AssetType.IMAGE,
                name: `${elemAsset.name}-rotated`,
                metadata: {
                    source: elemAsset.source,
                    attribution: elemAsset.attribution,
                    tags: elemAsset.tags,
                    link: elemAsset.link,
                },
                imagePropsOverride: {
                    hasAlpha: noBg,
                    hasSolidBackground: false,
                    imageBackgroundColor: false,
                    width: w,
                    height: h,
                },
                extra: {
                    alteredAssetId: originalAssetId,
                    noBg,
                    rotation: totalRotation,
                },
            });

            assetId = newAsset.id;

            progressDialog.setProgress(100);
            progressDialog.props.closeDialog();
        }

        // Update the asset id
        //const mediaElement = this.props.element;
        const contentElement = element.parentElement;
        const contentModel = contentElement.model;
        contentModel[contentElement.bindTo] = assetId;

        // Remove the background color if we have any image transparency
        if (
            noBg ||
            mediaOpacity < 1
        ) {
            contentElement.model.cellColor = "none";
        }

        trackActivity("PictureEditor", "FiltersApplied", null, null, {
            changed: this.trackChanged,
            delta: trackDelta,
        });

        element.updateFilterVersion();
        element.canvas.updateCanvasModel(false);
        closeDialog();
    }

    removeFilters = () => {
        const {
            originalRotation,
        } = this.state;

        this.orientPreviews({
            rotationOffset: 360 - originalRotation,
            flipHorizontal: false,
            noBg: false,
            filter: "none",
            filterBrightness: 1,
            filterContrast: 1,
            filterBlur: 0,
            mediaOpacity: 1,
            filterStrength: 1,
        });

        this.trackChanged.allFiltersReset = true;
    }

    onCancel = () => {
        trackActivity("PictureEditor", "FiltersCancelled", null, null, { changed: this.trackChanged });

        this.props.closeDialog();
    }

    getColors = () => {
        const { element } = this.props;
        return _.extend(
            {
                [PaletteColorType.THEME]: element.canvas.getTheme().palette.getColor(PaletteColorType.THEME).toHexString()
            },
            element.canvas.getTheme().palette.getAccentColors(),
            {
                [PaletteColorType.BACKGROUND_ACCENT]: element.canvas.getTheme().palette.getColor(PaletteColorType.BACKGROUND_ACCENT).toHexString()
            }
        );
    }

    render() {
        const { element } = this.props;
        const {
            rotationOffset,
            originalRotation,
            flipHorizontal,
            noBg,
            originalAssetUrl,
            altAssetUrl,
            gridColCount,
            thumbnailWidth,
            thumbnailHeight,
            previewWidth,
            previewHeight,
            filterBrightness,
            filterContrast,
            filterBlur,
            filterStrength,
            mediaOpacity,
            filter,
            activeFilterId,
            colors
        } = this.state;

        const assetUrl = noBg ? altAssetUrl : originalAssetUrl;
        const rotation = noBg ? rotationOffset : (originalRotation + rotationOffset) % 360;

        const filterId = `filter-preview--${activeFilterId}`;

        const filters = ["none", "brannan", "earlybird", "inkwell", "lofi", "mayfair", "nashville"];
        _.forIn(colors, (color, key) => {
            filters.push(key);
        });

        return (
            <BeautifulDialog
                maxWidth={false}
                closeDialog={this.onCancel}
            >
                <svg width={0} height={0}>
                    {renderAdjustmentFilter({ filterId }, this.state)}
                </svg>

                <Container>
                    <ImagePreview>
                        <FilterPreview
                            colors={colors}
                            width={previewWidth}
                            height={previewHeight}
                            filter={filter}
                            element={element}
                            assetUrl={assetUrl}
                            rotation={rotation}
                            flipHorizontal={flipHorizontal}
                            opacity={mediaOpacity}
                            filterStrength={filterStrength}
                            activeFilterId={activeFilterId}
                            showFilterName={false}
                        />
                    </ImagePreview>
                    <OptionsContainer>
                        <FilterContainer>
                            <header>Transform</header>
                            <TransformsContainer>
                                <RestyledIconButton
                                    size="small"
                                    onClick={() => {
                                        this.orientPreviews({ rotationOffset: (this.state.rotationOffset + 90) % 360 });
                                        this.trackChanged.rotated = true;
                                    }}
                                >
                                    <Icon>rotate_right</Icon>
                                    Rotate
                                </RestyledIconButton>
                                <RestyledIconButton
                                    size="small"
                                    onClick={() => {
                                        this.setState({ flipHorizontal: !this.state.flipHorizontal });
                                        this.trackChanged.flippedHorizontal = true;
                                    }}
                                >
                                    <Icon>flip</Icon>
                                    Flip
                                </RestyledIconButton>
                                <RestyledIconButton
                                    size="small"
                                    onClick={this.onToggleBg}
                                >
                                    <Icon>hide_image</Icon>
                                    Remove Background
                                </RestyledIconButton>
                            </TransformsContainer>
                            <header>Filters</header>
                            <StyledLabelContainer>
                                <SmallLabel>Brightness</SmallLabel>
                                <Slider
                                    value={filterBrightness}
                                    min={0.1}
                                    max={2}
                                    step={0.01}
                                    valueLabelDisplay="auto"
                                    onChange={(e, filterBrightness) => this.setStateAndRefreshEditor("brightness", { filterBrightness })}
                                />
                            </StyledLabelContainer>
                            <StyledLabelContainer>
                                <SmallLabel>Contrast</SmallLabel>
                                <Slider
                                    value={filterContrast}
                                    min={0.1}
                                    max={2}
                                    step={0.01}
                                    valueLabelDisplay="auto"
                                    onChange={(e, filterContrast) => this.setStateAndRefreshEditor("contrast", { filterContrast })}
                                />
                            </StyledLabelContainer>
                            <StyledLabelContainer>
                                <SmallLabel>Blur</SmallLabel>
                                <Slider
                                    value={filterBlur}
                                    min={0}
                                    max={100}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    onChange={(e, filterBlur) => this.setStateAndRefreshEditor("blur", { filterBlur })}
                                />
                            </StyledLabelContainer>
                            <StyledLabelContainer>
                                <SmallLabel>Opacity</SmallLabel>
                                <Slider
                                    value={(mediaOpacity * 100).toFixed(0)}
                                    min={1}
                                    max={100}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    onChange={(e, mediaOpacity) => this.setStateAndRefreshEditor("opacity", { mediaOpacity: parseFloat((mediaOpacity / 100).toFixed(2)) })}
                                />
                            </StyledLabelContainer>
                            <FlexSpacer />
                            <FilterGrid ref={this.gridRef}
                                style={{ gridTemplateColumns: `repeat(${gridColCount}, 1fr)` }}>
                                {filters.map((f, i) => (
                                    <FilterPreview
                                        colors={colors}
                                        key={`filter_${i}`}
                                        selected={f === filter}
                                        width={thumbnailWidth}
                                        height={thumbnailHeight}
                                        filter={f}
                                        element={element}
                                        assetUrl={assetUrl}
                                        rotation={rotation}
                                        flipHorizontal={flipHorizontal}
                                        opacity={mediaOpacity}
                                        filterStrength={filterStrength}
                                        activeFilterId={activeFilterId}
                                        onClick={() => this.setStateAndRefreshEditor("filter", { filter: f })}
                                    />
                                ))}
                            </FilterGrid>
                            <StyledLabelContainer>
                                <SmallLabel>Color</SmallLabel>
                                <Slider
                                    value={(filterStrength * 100).toFixed(0)}
                                    min={1}
                                    max={100}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    onChange={(e, filterStrength) => this.setStateAndRefreshEditor("filterStrength", { filterStrength: parseFloat((filterStrength / 100).toFixed(2)) })}
                                />
                            </StyledLabelContainer>
                            <FlexSpacer />
                        </FilterContainer>
                        <ButtonsContainer>
                            <Button
                                variant="text"
                                style={{
                                    color: "#FF4500",
                                }}
                                onClick={this.removeFilters}
                            >Reset Image</Button>
                            <FlexBox>
                                <Button
                                    variant="text"
                                    color="secondary"
                                    onClick={this.onCancel}
                                >Cancel</Button>
                                <Gap10 />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.applyFilters}
                                >Apply</Button>
                            </FlexBox>
                        </ButtonsContainer>
                    </OptionsContainer>

                </Container>

            </BeautifulDialog>
        );
    }
}

class FilterPreview extends Component {
    constructor() {
        super();

        this.ref = React.createRef();
    }

    componentDidUpdate = async prevProps => {
        if (
            !this.img ||
            prevProps.assetUrl !== this.props.assetUrl ||
            prevProps.width !== this.props.width ||
            prevProps.height !== this.props.height ||
            prevProps.rotation !== this.props.rotation
        ) {
            await this.resize();
            this.applyFilter();
        } else if (prevProps.filter !== this.props.filter || prevProps.filterStrength !== this.props.filterStrength) {
            this.applyFilter();
        }
    }

    resize = async () => {
        const {
            element,
            assetUrl,
            activeFilterId
        } = this.props;

        const width = element.asset.get("w");
        const height = element.asset.get("h");

        // Clear svg content
        this.ref.current.innerHTML = "";

        const draw = SVG(this.ref.current);
        this.group = draw.group();
        this.img = this.group.image(assetUrl);
        await new Promise(resolve => this.img.loaded(resolve));

        // Set image to fill the container using actual dimensions
        this.img.width(width);
        this.img.height(height);
        this.img.attr("preserveAspectRatio", "xMidYMid slice");

        // Apply the adjustment filter to the group
        this.group.node.setAttribute("filter", `url(#filter-preview--${activeFilterId}-adj-filter)`);

        // HACK: For Safari, make sure to identify this as the
        // node to refresh the filter ID for
        this.group.node.setAttribute("filter-preview", "");
    }

    applyFilter = async () => {
        const {
            filter,
            filterStrength,
            activeFilterId,
            colors
        } = this.props;

        // Apply the adjustment filter to the group if it exists
        if (this.group && this.group.node) {
            this.group.node.setAttribute("filter", `url(#filter-preview--${activeFilterId}-adj-filter)`);
        }

        switch (filter) {
            case "none":
                this.img.attr("filter", "");
                break;
            case "brannan":
            case "earlybird":
            case "inkwell":
            case "lofi":
            case "mayfair":
            case "nashville":
                this.img.attr("filter", `url(#${filter})`);
                break;
            default:
                this.img.filter(add => {
                    if (colors?.[filter]) {
                        const flood = add.flood(colors[filter], filterStrength ?? 1);
                        const grayscale = add.colorMatrix("saturate", 0);
                        add.blend(flood, grayscale, "multiply");
                    }
                });
        }
    }

    render() {
        const {
            element,
            width,
            height,
            rotation,
            flipHorizontal,
            opacity,
            filter,
            onClick,
            selected,
            showFilterName = true,
        } = this.props;

        const transpose = rotation % 180 !== 0;

        let scaleTransform = "";
        if (flipHorizontal) {
            scaleTransform = transpose ? "scaleY(-1)" : "scaleX(-1)";
        }

        let rotWidth = width;
        let rotHeight = height;
        if (transpose) {
            rotWidth = height;
            rotHeight = width;
        }

        let filterName;
        switch (filter) {
            case "background_accent":
                filterName = "bg accent";
                break;
            default:
                filterName = filter;
                break;
        }

        return (
            <FilterImage
                style={{
                    width,
                    height: height + (showFilterName ? 24 : 0),
                }}
                onClick={onClick}
                selected={selected}
            >
                <div
                    className="background"
                    style={{
                        width,
                        height,
                        flexDirection: width > height ? "row" : "column"
                    }}
                >
                    <div
                        style={{
                            width: rotWidth,
                            height: rotHeight,
                            transform: `rotate(${rotation}deg) ${scaleTransform}`,
                        }}
                    >
                        <svg
                            ref={this.ref}
                            width="100%"
                            height="100%"
                            preserveAspectRatio="xMidYMid slice"
                            viewBox={`0 0 ${element.asset.get("w")} ${element.asset.get("h")}`}
                            style={{
                                opacity
                            }}
                        ></svg>
                    </div>
                    {
                        selected &&
                        <SelectedFilter>
                            <Icon>check</Icon>
                        </SelectedFilter>
                    }
                </div>
                {
                    showFilterName &&
                    <label>{filterName}</label>
                }
            </FilterImage>
        );
    }
}

export const CreateAdjustImageMenu = function(view, contentElement, options = {}) {
    if (contentElement.options.useSmallContentUI) {
        view.$el.append(controls.createPopupButton(view, {
            icon: "photo_camera",
            className: "small-content-popup-button",
            label: options.label,
            showArrow: options.label != null,
            ...BuildAdjustImageMenu(contentElement, options)
        }));
    } else {
        return view.addControl(Object.assign({
            type: controls.POPUP_BUTTON,
            icon: "photo_camera",
            label: options.label,
            showArrow: options.label != null,
        }, BuildAdjustImageMenu(contentElement, options)));
    }
};

export const CreateImageFrameMenu = function(view, contentElement) {
    return view.addControl({
        id: "image-frame-menu",
        type: controls.POPUP_BUTTON,
        icon: "vignette",
        showArrow: false,
        menuClass: "icon-menu threecol",
        items: [{
            value: "none", label: "No Frame", image: getStaticUrl("/images/ui/frame_nocrop.svg")
        }, {
            value: "square", label: "Square", image: getStaticUrl("/images/ui/frame_square.svg")
        }, {
            value: "circle", label: "Circle", image: getStaticUrl("/images/ui/frame_circle.svg")
        }],
        callback: value => {
            contentElement.model.frameType = value;
            contentElement.markStylesAsDirty();
            contentElement.canvas.updateCanvasModel(false);
        }
    });
};

export function BuildAdjustImageMenu(contentElement, options = {}) {
    let items;
    if (contentElement.hasValidAsset) {
        let type = contentElement.model.content_type;

        items = [{
            value: "replace", label: `Replace ${type.toTitleCase()}...`, icon: "add_a_photo"
        }];

        if (options.allowAdjust !== false) {
            items.push({ value: "edit", label: `Adjust ${type.toTitleCase()}`, icon: "crop" });
        }

        items.push({ type: "divider" });
        items.push({ value: "remove", label: `Remove ${type.toTitleCase()}`, icon: "delete" });
    } else {
        let type = contentElement.options.defaultAssetType || AssetType.IMAGE;
        items = [{
            value: "replace", label: `Add Media...`, icon: "add_a_photo"
        }];
    }

    const selectionLayerController = contentElement.canvas.selectionLayerController;

    return {
        id: uuid(),
        items: items,
        callback: action => {
            switch (action) {
                case "replace":
                    ShowDialog(AddAssetsContainer, {
                        assetType: contentElement.model.content_type || contentElement.options.defaultAssetType || AssetType.IMAGE,
                        workspaceId: contentElement.canvas.slide.presentation.getWorkspaceId(),
                        backgroundVideoOnly: true,
                        callback: model => {
                            mergeMediaElementModelDefaults(
                                contentElement.model,
                                model,
                            );

                            if (options.onReplaceAsset) {
                                options.onReplaceAsset(model);
                            }

                            contentElement.canvas.updateCanvasModel(false);
                        },
                    });
                    break;
                case "edit":
                    selectionLayerController.setSelectedElements([contentElement.assetElement]);
                    break;
                case "filter":
                    selectionLayerController.setSelectedElements([contentElement.assetElement]);
                    break;
                case "remove":
                    contentElement.model.content_value = null;
                    contentElement.model.content_type = null;
                    if (options.onRemove) {
                        options.onRemove();
                    }
                    contentElement.canvas.updateCanvasModel(false).then(() => {
                        selectionLayerController.setSelectedElements([]);
                    });
                    break;
            }
        }
    };
}

export const CreateVideoMenu = function(view, contentElement, options = {}) {
    view.addControl(Object.assign({
        type: controls.POPUP_BUTTON,
        icon: "photo_camera",
        showArrow: false,
    }, BuildVideoSourceMenu(contentElement, options)));
};

export function BuildVideoSourceMenu(contentElement, options = {}) {
    let items;
    if (contentElement.hasValidAsset) {
        let type = contentElement.model.content_type;

        items = [{
            value: "replace", label: `Replace Video...`, icon: "video_library"
        }];

        items.push({ value: "edit", label: `Adjust Video`, icon: "crop" });
        items.push({ type: "divider" });
        items.push({ value: "remove", label: `Remove Video`, icon: "delete" });
    } else {
        let type = contentElement.options.defaultAssetType || AssetType.VIDEO;
        items = [{
            value: "replace", label: `Add Video...`, icon: "video_library"
        }];
    }

    const selectionLayerController = contentElement.canvas.selectionLayerController;

    return {
        items: items,
        callback: action => {
            switch (action) {
                case "replace":
                    ShowDialog(AddAssetsContainer, {
                        assetType: contentElement.model.content_type || contentElement.options.defaultAssetType || AssetType.VIDEO,
                        workspaceId: contentElement.canvas.slide.presentation.getWorkspaceId(),
                        backgroundVideoOnly: true,
                        callback: model => {
                            mergeMediaElementModelDefaults(
                                contentElement.model,
                                model,
                            );

                            contentElement.canvas.updateCanvasModel(false);
                        },
                    });
                    break;
                case "edit":
                    selectionLayerController.setSelectedElements([contentElement.assetElement]);
                    break;
                case "filter":
                    selectionLayerController.setSelectedElements([contentElement.assetElement]);
                    break;
                case "remove":
                    contentElement.model.content_value = null;
                    contentElement.model.content_url = null;
                    contentElement.model.content_type = null;
                    if (options.onRemove) {
                        options.onRemove();
                    }
                    contentElement.canvas.updateCanvasModel(false).then(() => {
                        selectionLayerController.setSelectedElements([]);
                    });
                    break;
            }
        }
    };
}

export function BuildVideoSettingsMenu(videoElement, options = {}) {
    const { canBeBackgroundVideo } = videoElement;

    const refreshCanvasAndSaveChanges = (save = true) => {
        save && videoElement.canvas.saveCanvasModel();
        videoElement.canvas.refreshElement(videoElement, null, true);
    };

    const refreshList = [];
    const refreshMenu = () => {
        refreshList.forEach(refresh => refresh());
    };

    const items = [
        {
            type: "control",
            icon: "volume_up",
            view: () => {
                const $menu = $.div();
                const $reactWrapper = $menu.addEl($.div("control"));

                const handleChangeVideoMuted = event => {
                    if (videoElement) {
                        videoElement.model.assetProps.muted = !event.target.checked;
                        videoElement.toggleAudio({ value: !videoElement.model.assetProps.muted, refresh: false });
                    }
                    refreshCanvasAndSaveChanges();
                    refresh();
                };

                const refresh = () => {
                    const jsx = (
                        <LabeledContainer label="Audio">
                            <Switch
                                name="muted"
                                color="primary"
                                checked={!(videoElement.model.assetProps?.muted || false)}
                                onChange={handleChangeVideoMuted}
                                disabled={!videoElement.canPlayAudio}
                            />
                        </LabeledContainer>
                    );
                    renderReactRootJSX(jsx, $reactWrapper[0]);
                };
                refreshList.push(refresh);
                refresh();
                return $menu;
            },
        },
        {
            type: "control",
            icon: "loop",
            view: () => {
                const $menu = $.div();
                const $reactWrapper = $menu.addEl($.div("control"));

                const handleChangeVideoLoop = event => {
                    const loop = event.target.checked;
                    if (videoElement) {
                        videoElement.model.assetProps.loop = loop;
                        if (!videoElement.isPlaying && loop) {
                            videoElement.togglePlayback({ value: true, refresh: false });
                        }
                    }
                    refreshCanvasAndSaveChanges();
                    refresh();
                };

                const refresh = () => {
                    const jsx = (
                        <LabeledContainer label="Loop Video">
                            <Switch
                                name="loop"
                                color="primary"
                                checked={videoElement.model.assetProps?.loop || false}
                                onChange={handleChangeVideoLoop}
                                disabled={!canBeBackgroundVideo}
                            />
                        </LabeledContainer>
                    );
                    renderReactRootJSX(jsx, $reactWrapper[0]);
                };
                refreshList.push(refresh);
                refresh();
                return $menu;
            },
        },
        {
            type: "control",
            icon: "timer",
            view: () => {
                const $menu = $.div();
                const $reactWrapper = $menu.addEl($.div("control"));

                const handleChangeVideoTime = (value, save = true) => {
                    const [
                        startTime,
                        endTime,
                    ] = value;

                    if (videoElement) {
                        videoElement.model.assetProps.startTime = startTime;
                        videoElement.model.assetProps.endTime = endTime;
                    }
                    refreshCanvasAndSaveChanges(save);
                    videoElement.togglePlayback({
                        value: startTime < endTime,
                        refresh: false,
                        reset: true,
                    });
                    // refresh(); // Not needed for InputSlider
                };

                const refresh = () => {
                    const jsx = (
                        <LabeledContainer label="Video Range">
                            <div style={{ paddingRight: "10px" }}>
                                <MultiInputSlider
                                    value={[
                                        videoElement.model.assetProps?.startTime || 0,
                                        videoElement.model.assetProps?.endTime || videoElement.model.assetProps?.duration,
                                    ]}
                                    multiValue
                                    onChange={values => handleChangeVideoTime(values, false)}
                                    onChangeCommitted={values => handleChangeVideoTime(values)}
                                    sliderMin={0}
                                    sliderMax={videoElement.model.assetProps?.duration}
                                    inputMin={0}
                                    inputMax={videoElement.model.assetProps?.duration}
                                    step={1}
                                />
                            </div>
                        </LabeledContainer>
                    );
                    renderReactRootJSX(jsx, $reactWrapper[0]);
                };
                refreshList.push(refresh);
                refresh();
                return $menu;
            },
        },
        {
            type: "control",
            icon: "speed",
            view: () => controls.createDropdownMenu(this, {
                label: "Speed",
                items: [
                    { value: 2.0, label: "2.0x" },
                    { value: 1.75, label: "1.75x" },
                    { value: 1.5, label: "1.5x" },
                    { value: 1.25, label: "1.25x" },
                    { value: 1.0, label: "1.0x" },
                    { value: 0.75, label: "0.75x" },
                    { value: 0.5, label: "0.5x" },
                    { value: 0.25, label: "0.25x" },
                ],
                property: "speed",
                model: videoElement.model.assetProps,
                enabled: canBeBackgroundVideo,
            }),
        },
    ];

    return {
        items: items,
    };
}

export const CreateMediaMenu = function(view, contentElement, options = {}) {
    const type = contentElement.model.content_type;

    switch (type) {
        case AssetType.VIDEO:
        case AssetType.STOCK_VIDEO:
            return CreateVideoMenu(view, contentElement, options);
        default:
            return CreateAdjustImageMenu(view, contentElement, options);
    }
};
